import {
    Button,
    LoadingIndicator,
    LoadingSkeleton,
    Message,
} from '@ics-portal/guardian';
import type { EmptyStateConfig, ResolvedColumn } from '../../domain';
import { Icon } from '../shared/Icon';
import { columnStyle } from './Body';

export interface EmptyProps {
    config?: EmptyStateConfig;
    filtered: boolean;
}

export function Empty(props: EmptyProps): JSX.Element {
    const { config, filtered } = props;

    const defaultTitle = config?.title ?? 'No records found.';
    let title = defaultTitle;
    if (filtered) {
        title =
            config?.noResultsTitle ??
            (config ? defaultTitle : 'No records match your search.');
    }

    return (
        <div className="ics-table__empty" role="status">
            {config?.icon ? (
                <div className="ics-table__empty-icon">{config.icon}</div>
            ) : null}
            <p className="ics-table__empty-title">{title}</p>
            {config?.description ? (
                <p className="ics-table__empty-description">
                    {config.description}
                </p>
            ) : null}
        </div>
    );
}

const DEFAULT_SKELETON_ROWS = 8;

export interface SkeletonSelectConfig {
    mode: 'single' | 'multi';
}

export interface SkeletonExpandConfig {
    showCount?: boolean;
}

export interface SkeletonRowsProps<T> {
    columns: ResolvedColumn<T>[];
    select?: SkeletonSelectConfig;
    expand?: SkeletonExpandConfig;
    menu?: boolean;
    rowCount?: number;
}

export function SkeletonRows<T>(props: SkeletonRowsProps<T>): JSX.Element {
    const {
        columns,
        select,
        expand,
        menu,
        rowCount = DEFAULT_SKELETON_ROWS,
    } = props;

    return (
        <>
            {Array.from({ length: rowCount }, (_, rowIndex) => (
                <tr
                    key={rowIndex}
                    className="ics-table__row"
                    aria-hidden="true"
                >
                    {select ? (
                        <td className="ics-table__cell ics-table__cell--select">
                            <span className="ics-table__select-wrap">
                                <LoadingSkeleton
                                    elements={{
                                        shape: 'rectangle',
                                        width: '16px',
                                        height: '16px',
                                    }}
                                />
                            </span>
                        </td>
                    ) : null}
                    {expand ? (
                        <td className="ics-table__cell ics-table__cell--expand">
                            <span className="ics-table__expand-toggle">
                                <LoadingSkeleton
                                    elements={{ shape: 'circle', size: '1rem' }}
                                />
                                {expand.showCount ? (
                                    <LoadingSkeleton
                                        elements={{
                                            shape: 'rectangle',
                                            width: '1.5rem',
                                            height: '0.9rem',
                                        }}
                                    />
                                ) : null}
                            </span>
                        </td>
                    ) : null}
                    {columns.map((col) => (
                        <td
                            key={col.id}
                            style={columnStyle(col)}
                            className="ics-table__cell"
                        >
                            <LoadingSkeleton
                                elements={{ height: '1rem', width: '70%' }}
                            />
                        </td>
                    ))}
                    {menu ? (
                        <td className="ics-table__cell ics-table__cell--menu">
                            <LoadingSkeleton
                                elements={{ shape: 'circle', size: '1.25rem' }}
                            />
                        </td>
                    ) : null}
                </tr>
            ))}
        </>
    );
}

export interface LoadingProps {
    label?: string;
}

export function Loading(props: LoadingProps): JSX.Element {
    const { label } = props;

    return (
        <div
            className="ics-table__loading ics-table__loading--overlay"
            role="status"
            aria-live="polite"
        >
            <LoadingIndicator variant="ripple" label={label} />
        </div>
    );
}

function errorMessage(error: unknown): string {
    return error instanceof Error ? error.message : 'Something went wrong.';
}

export interface FailedProps {
    error: unknown;
    onRetry: () => void;
}

export function Failed(props: FailedProps): JSX.Element {
    const { error, onRetry } = props;

    return (
        <div className="ics-table__empty" role="alert">
            <p className="ics-table__empty-title">Something went wrong.</p>
            <p className="ics-table__empty-description">
                {errorMessage(error)}
            </p>
            <Button severity="secondary" onClick={onRetry}>
                Retry
            </Button>
        </div>
    );
}

export interface StaleErrorProps {
    error: unknown;
    onRetry: () => void;
}

export function StaleError(props: StaleErrorProps): JSX.Element {
    const { error, onRetry } = props;

    return (
        <Message
            severity="error"
            className="ics-table__stale-error"
            content={
                <>
                    <Icon name="error" />
                    <span>{errorMessage(error)}</span>
                    <Button severity="secondary" onClick={onRetry}>
                        Retry
                    </Button>
                </>
            }
        />
    );
}
